<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">

    <div class="bg-white dark:bg-slate-900 w-full max-w-md rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col">

      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
        <div>
          <h2 class="text-lg font-bold text-slate-900 dark:text-white">
            {{ form.id ? 'Editar Bodega' : 'Nueva Bodega' }}
          </h2>
          <p class="text-xs text-slate-500">Define una ubicación física para el stock.</p>
        </div>
        <button @click="$emit('close')" class="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
          <X class="w-5 h-5" />
        </button>
      </div>

      <form @submit.prevent="handleSubmit" class="p-6 space-y-5">

        <div>
          <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Sucursal</label>
          <div class="relative">
            <Building class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <select v-model="form.branch" required class="form-input-premium pl-10 appearance-none">
              <option value="" disabled>Seleccionar Sucursal</option>
              <option v-for="b in branchesList" :key="b.id" :value="b.id">{{ b.name }}</option>
            </select>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div class="col-span-2 sm:col-span-1">
            <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Código</label>
            <div class="relative">
              <Hash class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input v-model="form.code" type="text" class="form-input-premium pl-10 uppercase" placeholder="BOD-01" />
            </div>
          </div>
          <div class="col-span-2 sm:col-span-1">
            <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Nombre</label>
            <input v-model="form.name" type="text" required class="form-input-premium" placeholder="Principal" />
          </div>
        </div>

        <div>
          <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Descripción</label>
          <textarea v-model="form.description" rows="2" class="form-input-premium resize-none" placeholder="Estantería A, Pasillo 3..."></textarea>
        </div>

        <div class="space-y-3 pt-2">
          <label class="flex items-center justify-between p-3 rounded-xl border border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors">
            <div>
              <span class="block text-sm font-medium text-slate-700 dark:text-slate-200">Disponible para Venta</span>
              <span class="text-xs text-slate-500">El POS puede vender productos de aquí.</span>
            </div>
            <div class="relative">
              <input type="checkbox" v-model="form.is_saleable" class="sr-only peer">
              <div class="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
            </div>
          </label>

          <label class="flex items-center justify-between p-3 rounded-xl border border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors">
            <div>
              <span class="block text-sm font-medium text-slate-700 dark:text-slate-200">Bodega Activa</span>
              <span class="text-xs text-slate-500">Permitir entradas y salidas.</span>
            </div>
            <div class="relative">
              <input type="checkbox" v-model="form.is_active" class="sr-only peer">
              <div class="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
            </div>
          </label>
        </div>

        <div class="pt-4 flex justify-end gap-3 border-t border-slate-100 dark:border-slate-800">
          <button
            type="button"
            @click="$emit('close')"
            class="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600 dark:hover:bg-slate-700 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            :disabled="isSaving"
            class="px-6 py-2 text-sm font-bold text-white bg-brand-600 rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/30 transition-all flex items-center gap-2 disabled:opacity-70"
          >
            <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
            <Save v-else class="w-4 h-4" />
            Guardar
          </button>
        </div>

      </form>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, watch, onMounted } from 'vue';
import { X, Save, Loader2, Building, Hash } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import { useToast } from '@/composables/useToast';

const props = defineProps({
  show: Boolean,
  locationToEdit: Object
});

const emit = defineEmits(['close', 'saved']);
const { showSuccess, showError, showWarning } = useToast();

const isSaving = ref(false);
const branchesList = ref([]);
const currentUser = SecureStorage.getItem('llevo_user');
const currentCompanyId = currentUser?.company; // Obtenemos la compañía

const form = reactive({
  id: null,
  company: currentCompanyId, // <--- AQUI SE AGREGA "POR DEBAJO"
  branch: '',
  name: '',
  code: '',
  description: '',
  is_saleable: true,
  is_active: true
});

// Cargar Sucursales
onMounted(async () => {
  try {
    if (currentCompanyId) {
      const res = await apiClient.get(`/branches?company=${currentCompanyId}`);
      branchesList.value = Array.isArray(res.data) ? res.data : (res.data.data || []);
    }
  } catch (e) {
    console.error("Error cargando sucursales", e);
  }
});

watch(() => props.locationToEdit, (newVal) => {
  if (newVal) {
    Object.assign(form, {
      id: newVal.id,
      company: newVal.company || currentCompanyId, // Mantener o asignar
      branch: newVal.branch,
      name: newVal.name,
      code: newVal.code,
      description: newVal.description,
      is_saleable: Boolean(newVal.is_saleable),
      is_active: Boolean(newVal.is_active)
    });
  } else {
    // Reset para crear
    Object.assign(form, {
      id: null,
      company: currentCompanyId, // Reiniciar con la compañía actual
      branch: '',
      name: '',
      code: '',
      description: '',
      is_saleable: true,
      is_active: true
    });
  }
});

const handleSubmit = async () => {
  if (!form.name.trim() || !form.branch) {
    showWarning('La Sucursal y el Nombre son obligatorios.');
    return;
  }

  isSaving.value = true;
  try {
    const payload = { ...form };

    // endpoint: /locations (nombre de la tabla en tu whitelist de PHP)
    if (form.id) {
      await apiClient.put(`/locations/${form.id}`, payload);
      showSuccess('Bodega actualizada.');
    } else {
      await apiClient.post('/locations', payload);
      showSuccess('Bodega creada.');
    }
    emit('saved');
    emit('close');
  } catch (error) {
    console.error(error);
    showError('Error al guardar la bodega.');
  } finally {
    isSaving.value = false;
  }
};
</script>

<style scoped>
@keyframes fade-in {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
.animate-fade-in {
  animation: fade-in 0.2s ease-out forwards;
}
</style>